<template>
  <div class="admin-layout">
    <div
      v-if="isSidebarOpen"
      class="sidebar-overlay"
      @click="closeSidebar"
      aria-hidden="true"
    />
    <AdminSidebar :is-open="isSidebarOpen" @close="closeSidebar" />
    <main class="admin-main-content">
      <header class="admin-header">
        <button
          type="button"
          class="sidebar-toggle"
          aria-label="Toggle menu"
          :aria-expanded="isSidebarOpen"
          @click="toggleSidebar"
        >
          <span class="toggle-bar"></span>
          <span class="toggle-bar"></span>
          <span class="toggle-bar"></span>
        </button>
        <h2>{{ currentRouteName }}</h2>
      </header>
      <div class="admin-page-content">
        <router-view />
      </div>
    </main>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import AdminSidebar from '@/components/AdminSidebar.vue';

export default defineComponent({
  name: 'AdminLayout',
  components: {
    AdminSidebar,
  },
  setup() {
    const route = useRoute();
    const isSidebarOpen = ref(false);

    const currentRouteName = computed(() => {
      const name = route.name as string;
      if (route.meta && route.meta.title) {
        return route.meta.title as string;
      }
      return name ? name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, ' $1').trim() : 'Dashboard';
    });

    const toggleSidebar = () => {
      isSidebarOpen.value = !isSidebarOpen.value;
    };

    const closeSidebar = () => {
      isSidebarOpen.value = false;
    };

    watch(() => route.path, () => {
      closeSidebar();
    });

    return {
      currentRouteName,
      isSidebarOpen,
      toggleSidebar,
      closeSidebar,
    };
  },
});
</script>

<style scoped>
.admin-layout {
  display: flex;
  flex-grow: 1;
  background-color: #f4f7f6;
  min-height: calc(100vh - var(--header-height));
  position: relative;
}

.sidebar-overlay {
  display: none;
}

.admin-main-content {
  margin-left: var(--sidebar-width);
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: calc(100% - var(--sidebar-width));
  min-width: 0;
  overflow: hidden;
}

.admin-header {
  background-color: #fff;
  padding: 12px 24px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 999;
  flex-shrink: 0;
}

.admin-header h2 {
  margin: 0;
  font-size: 1.25rem;
  color: var(--brand-black, #333);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: var(--touch-min);
  height: var(--touch-min);
  padding: 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
}

.toggle-bar {
  display: block;
  width: 22px;
  height: 2px;
  background: var(--brand-black, #333);
  border-radius: 1px;
}

.admin-page-content {
  padding: 24px;
  flex-grow: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

/* Responsive admin child pages (tables, headers, filters) */
.admin-page-content :deep(h1) {
  font-size: clamp(1.35rem, 4vw, 1.75rem);
}

.admin-page-content :deep(.view-header),
.admin-page-content :deep(.page-header) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.admin-page-content :deep(.search-and-filter) {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  flex: 1 1 280px;
}

.admin-page-content :deep(.search-input),
.admin-page-content :deep(.filter-select) {
  min-width: 0;
  flex: 1 1 140px;
}

.admin-page-content :deep(.actions) {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.admin-page-content :deep(.action-button) {
  white-space: nowrap;
}

@media (max-width: 992px) {
  .sidebar-overlay {
    display: block;
    position: fixed;
    inset: var(--header-height) 0 0 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 1001;
  }

  .admin-main-content {
    margin-left: 0;
    width: 100%;
  }

  .sidebar-toggle {
    display: flex;
  }

  .admin-page-content {
    padding: 16px;
  }

  .admin-page-content :deep(.view-header),
  .admin-page-content :deep(.page-header) {
    flex-direction: column;
    align-items: stretch;
  }

  .admin-page-content :deep(.search-and-filter) {
    flex-direction: column;
    align-items: stretch;
  }

  .admin-page-content :deep(.search-input),
  .admin-page-content :deep(.filter-select) {
    width: 100%;
    flex: none;
  }

  .admin-page-content :deep(.actions) {
    width: 100%;
  }

  .admin-page-content :deep(.actions .btn) {
    flex: 1;
    min-width: 0;
  }

  .admin-page-content :deep(.cells-actions) {
    min-width: unset !important;
  }
}

@media (max-width: 576px) {
  .admin-header {
    padding: 10px 12px;
  }

  .admin-header h2 {
    font-size: 1.1rem;
  }

  .admin-page-content {
    padding: 12px;
  }
}
</style>
